/**
 * Modal dialog for confirming account deletion.
 * Warns the user that the action is permanent and irreversible.
 *
 * @module components/DeleteAccountModal
 */

import React, { useState } from 'react';
import Modal               from './Modal';
import { useI18n }         from '../dataProviders/I18nProvider';
import { useAuth }         from '../hooks/useAuth';
import apiClient           from '../procedural/apiClient';

/**
 * Renders a delete-account confirmation modal.
 *
 * @param {object}   props
 * @param {Function} props.onClose - Called to dismiss the modal.
 * @returns {React.ReactElement}
 */
export default function DeleteAccountModal({ onClose }) {
  const { t }    = useI18n();
  const { logout } = useAuth();

  const [error, setError]          = useState(null);
  const [submitting, setSubmitting] = useState(false);

  /**
   * Delete the account and redirect to home.
   */
  async function handleDelete() {
    setError(null);
    setSubmitting(true);
    try {
      await apiClient.delete('/api/settings/account');
      await logout();
    } catch {
      setError(t('settings.deleteAccount.error'));
      setSubmitting(false);
    }
  }

  return (
    <Modal title={t('settings.deleteAccount.title')} onClose={onClose}>
      <p style={{ color: 'var(--color-danger)', marginBottom: '12px', lineHeight: 1.6 }}>
        {t('settings.deleteAccount.warning')}
      </p>
      <p style={{ fontWeight: 600, marginBottom: '4px' }}>
        {t('settings.deleteAccount.confirm')}
      </p>

      {error && <div className="auth-error" style={{ marginTop: '12px' }}>{error}</div>}

      <div className="modal-actions">
        <button type="button" className="modal-btn modal-btn--cancel" onClick={onClose} disabled={submitting}>
          {t('settings.deleteAccount.cancel')}
        </button>
        <button type="button" className="modal-btn modal-btn--danger" onClick={handleDelete} disabled={submitting}>
          {t('settings.deleteAccount.delete')}
        </button>
      </div>
    </Modal>
  );
}
